<template>
  <section class="w-full">
    <h1 class="flex items-center justify-between">
      {{ $t('components.company.baseData.details') }}
      <div class="text-sm text-gray-600">
        <button
          class="font-semibold uppercase hover:text-gray-800"
          @click="showCompanySlugModal()"
        >
          {{ $t('components.company.baseData.editSlug') }}
        </button>
        <span class="mx-3 font-bold">|</span>
        <button
          class="font-semibold uppercase hover:text-gray-800"
          @click="showCompanyDeleteModal()"
        >
          {{ $t('components.company.baseData.delete') }}
        </button>
        <span class="mx-3 font-bold">|</span>
        <router-link
          :to="`/companies/${company.id}/update`"
          class="inline-block font-semibold uppercase hover:text-gray-800"
        >
          {{ $t('components.company.baseData.edit') }}
        </router-link>
      </div>
    </h1>

    <div class="flex flex-wrap w-full -mx-4">
      <div class="w-full p-4 border-r-2 lg:w-1/2">
        <data-field
          :label="$t('components.company.baseData.companyName')"
          :value="company.name"
          required
        />
        <data-field
          :label="$t('components.company.baseData.slogan')"
          :value="company.slogan"
        />
        <data-field
          :label="$t('components.company.baseData.slug')"
          :value="company.slug"
          required
        />
        <data-field
          :label="$t('components.company.baseData.countryCode')"
          :value="countryFlag"
          required
        />
        <data-field
          :label="$t('components.company.baseData.address')"
          :has-value="Boolean(company.address && company.address.street && company.address.zip && company.address.city && company.address.country)"
          required
        >
          <ul v-if="company.address">
            <li>{{ company.address.street }}</li>
            <li>{{ company.address.zip }} {{ company.address.city }}</li>
            <li>{{ company.address.country }}</li>
          </ul>
        </data-field>

        <data-field
          :label="$t('components.company.baseData.regionName')"
          :value="company.regionName"
        />
        <data-field :label="$t('components.company.baseData.commission')">
          <span v-if="company.commission">{{ company.commission }} %</span>
          <em
            v-else
            class="text-gray-500"
          >{{ $t('components.company.baseData.notConfigured') }}</em>
        </data-field>
        <Feature :feature-slug="featureNames.CLIENT_ADVISOR">
          <data-field
            :label="$t('components.company.baseData.clientAdvisor')"
            :value="company.clientAdvisor && company.clientAdvisor.name"
          />
        </Feature>
        <Feature :feature-slug="featureNames.SALES_PERSON">
          <data-field
            :label="$t('components.company.baseData.salesPerson')"
            :value="company.salesPerson && company.salesPerson.name"
          />
        </Feature>
        <Feature
          v-slot="{feature}"
          :feature-slug="featureNames.BILLWERK"
        >
          <div>
            <data-field
              v-if="feature.isActive"
              :label="$t('components.company.baseData.debtorId')"
              :value="company.debtorId"
              required
            />
            <data-field
              else
              :label="$t('components.company.baseData.customerNumber')"
              :value="company.customerNumber"
              required
            />
          </div>
        </Feature>

        <Feature
          v-slot="{feature}"
          :feature-slug="featureNames.COMPANY"
        >
          <data-field
            v-if="feature.config.isLogoEditable"
            :label="$t('components.company.baseData.logoReleased')"
            :value="company.isReferenceCustomer ? $t('components.company.baseData.yes') : $t('components.company.baseData.no')"
          />
        </Feature>
      </div>

      <div class="w-full p-4 lg:w-1/2">
        <data-field
          class="overflow-x-scroll"
          :label="$t('components.company.baseData.hasInitialWebsite')"
          :has-value="true"
          required
        >
          <em
            class="text-gray-500"
          >{{ company.hasNoInitialWebsite ? $t('components.company.baseData.no') : $t('components.company.baseData.yes') }}</em>
        </data-field>
        <div
          v-for="link in LINK_LABELS"
          :key="link.key"
          class="flex w-full mb-1"
        >
          <data-field
            class="overflow-x-scroll"
            :label="link.name"
            :has-value="Boolean(company.links && company.links[link.key])"
            required
          >
            <a
              v-if="company.links[link.key]"
              :href="company.links[link.key]"
              target="_blank"
              rel="noopener noreferrer"
              class="underline"
            >{{ company.links[link.key] }}</a>
            <em
              v-else
              class="text-gray-500"
            >{{ $t('components.company.baseData.notConfigured') }}</em>
          </data-field>
        </div>

        <data-field :label="$t('components.company.baseData.performance')">
          <a
            v-if="company.googlePerformanceReportId"
            :href="`https://datastudio.google.com/open/${company.googlePerformanceReportId}`"
            target="_blank"
            class="underline"
            rel="external noopener noreferrer"
          >{{ $t('pages.companies.update.googlePerformanceReport') }}</a>
          <em
            v-else
            class="text-gray-500"
          >{{ $t('components.company.baseData.notConfigured') }}</em>
        </data-field>

        <data-field
          :label="$t('components.company.baseData.bccEmail')"
          :value="company.leadCorrespondenceBccEmail"
        />
        <div>
          <data-field
            :label="$t('pages.companies.update.advertisementId')"
            :value="company.advertisementId"
          />
          <data-field
            :label="$t('pages.companies.update.fbPixelId')"
            :value="company.fbPixelId"
          />
          <data-field
            :label="$t('pages.companies.update.fbBusinessManagerId')"
            :value="company.fbBusinessManagerId"
          />
          <data-field :label="$t('components.company.baseData.pixelEventSource')">
            {{ formatFbPixelEventSourceUrlType(facebook?.pixelEventSourceUrlType) }}
            <span v-if="facebook?.pixelEventSourceUrlType === 'CUSTOM'">
              ({{ facebook?.customPixelEventSourceUrl }})
            </span>
          </data-field>
          <data-field :label="$t('components.company.baseData.pixelAccessToken')">
            <span v-if="company.hasFbPixelAccessToken">{{ $t('components.company.baseData.tokenSet') }} <br></span>
            <button
              class="font-semibold hover:text-gray-800"
              @click="showFbPixelAccessTokenModal()"
            >
              <fa :icon="faPen" />
              {{ $t('components.company.baseData.setToken') }}
            </button>
            {{ $t('components.company.baseData.securityNotice') }}
          </data-field>
        </div>

        <div
          v-for="(social, index) in company.socials"
          :key="index"
        >
          <data-field :label="social.type">
            <a
              class="w-3/5 overflow-hidden"
              :href="social.link"
              target="_blank"
            >{{ social.link }}</a>
          </data-field>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import DeleteCompanyModal from '@/components/modals/company/delete'
import UpdateCompanySlugModal from '@/components/modals/company/updateSlug'
import { faPen, faPlus } from '@fortawesome/free-solid-svg-icons'
import dataField from '../data-field.vue'
import { getCountries, getLinkLabels } from '@/config'
import feature from '@/mixins/feature'
import FbPixelAccessTokenModal from '@/components/modals/company/performance/fbPixelAccessToken'

export default {
  components: { dataField },
  mixins: [feature],
  props: {
    company: { type: Object, default: () => ({}) }
  },
  data () {
    return { faPen, faPlus }
  },
  computed: {
    LINK_LABELS () {
      // eslint-disable-next-line no-void
      void this.$i18n.locale
      return getLinkLabels()
    },
    countryFlag () {
      return getCountries()[this.company.country] || ''
    },
    facebook () {
      return this.company?.performance?.facebook
    }
  },
  methods: {
    showCompanyDeleteModal () {
      this.$modal.show(
        DeleteCompanyModal,
        {
          companyId: this.company.id,
          companyName: this.company.name,
          companySlug: this.company.slug
        },
        { height: 'auto' }
      )
    },
    showCompanySlugModal () {
      this.$modal.show(
        UpdateCompanySlugModal,
        {
          companyId: this.company.id,
          companyName: this.company.name
        },
        { height: 'auto' }
      )
    },
    formatFbPixelEventSourceUrlType (value) {
      return ({
        COMPANY_WEBSITE: this.$t('components.company.baseData.companyWebsite'),
        LANDINGPAGE_DEFAULT: this.$t('components.company.baseData.landingpageDefault'),
        CUSTOM: this.$t('components.company.baseData.custom')
      })[value]
    },
    showFbPixelAccessTokenModal () {
      this.$modal.show(
        FbPixelAccessTokenModal,
        { companyId: this.company.id }
      )
    }
  }
}
</script>
